import { Component, inject, OnInit } from '@angular/core';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { MobileReleaseVersion, MobileReleaseVersionsService } from './mobile-release-versions.service';

@Component( {
    selector: 'app-mobile-release-versions',
    standalone: false,
    templateUrl: './mobile-release-versions.component.html',
    styleUrls: [ './mobile-release-versions.component.scss' ]
} )
export class MobileReleaseVersionsComponent implements OnInit {

    private _releaseVersionsService = inject(MobileReleaseVersionsService);
    private _globals = inject(GlobalsService);

    protected versions: MobileReleaseVersion[] = [];
    protected filteredVersions: MobileReleaseVersion[] = [];
    protected selected: MobileReleaseVersion = this.createBlankVersion();
    protected searchText: string = '';
    protected loading: boolean = false;
    protected saving: boolean = false;
    protected formActive: boolean = false;
    protected isNewVersion: boolean = true;

    protected get viewEditMobileReleaseVersions(): boolean {
        return this._globals.user?.appClaims?.viewEditMobileReleaseVersions === true;
    }


    ngOnInit(): void {
        this.loadVersions();
    }

    loadVersions(): void {
        this.loading = true;

        this._releaseVersionsService.getAllVersions().subscribe( {
            next: versions => {
                this.versions = versions || [];
                this.applySearch();
                this.loading = false;
            },
            error: error => {
                this.loading = false;
                this._globals.notifyError( 'Unable to load mobile release versions.', true );
                console.error( error );
            }
        } );
    }

    searchChanged(): void {
        this.applySearch();
    }

    newVersion(): void {
        if ( ! this.viewEditMobileReleaseVersions ) {
            this._globals.notifyWarning( 'You do not have permission to edit mobile release versions.', true );
            return;
        }

        this.selected = this.createBlankVersion();
        this.isNewVersion = true;
        this.formActive = true;
    }

    edit( version: MobileReleaseVersion ): void {
        if ( ! this.viewEditMobileReleaseVersions ) {
            this._globals.notifyWarning( 'You do not have permission to edit mobile release versions.', true );
            return;
        }

        this.selected = { ... version };
        if ( this.selected.dateReleased ) {
            this.selected.dateReleased = this.toDate( this.selected.dateReleased );
        }
        this.isNewVersion = false;
        this.formActive = true;
    }

    closeEditor(): void {
        this.formActive = false;
        this.saving = false;
        this.selected = this.createBlankVersion();
    }

    save(): void {
        if ( ! this.viewEditMobileReleaseVersions ) {
            this._globals.notifyWarning( 'You do not have permission to edit mobile release versions.', true );
            return;
        }

        if ( ! this.selected.versionNumber || ! this.selected.versionNumber.trim() ) {
            this._globals.notifyWarning( 'Version Number is required.', true );
            return;
        }

        this.saving = true;

        this._releaseVersionsService.save( this.selected ).subscribe( {
            next: () => {
                this.saving = false;
                this.formActive = false;
                this._globals.notifySuccess( 'Mobile release version saved.' );
                this.selected = this.createBlankVersion();
                this.loadVersions();
            },
            error: error => {
                this.saving = false;
                this._globals.notifyError( 'Unable to save mobile release version.', true );
                console.error( error );
            }
        } );
    }

    private applySearch(): void {
        const search: string = (this.searchText || '').trim().toLowerCase();
        if ( ! search ) {
            this.filteredVersions = [ ... this.versions ];
            return;
        }

        this.filteredVersions = this.versions.filter( version =>
            (version.versionNumber || '').toLowerCase().indexOf( search ) >= 0
        );
    }

    private createBlankVersion(): MobileReleaseVersion {
        return {
            versionNumber: '',
            dateReleased: new Date(),
            active: false
        };
    }

    private toDate( value: string | Date ): Date {
        const date: Date = value instanceof Date ? value : new Date( value );
        return isNaN( date.getTime() ) ? null : date;
    }
}
